import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  MagnifyingGlass,
  ListMagnifyingGlass,
  Table as TableIcon,
  SquaresFour,
  ArrowsDownUp,
  Printer,
  Selection,
  SelectionSlash,
  ClockCounterClockwise,
  TrendUp,
  Prohibit,
  Eye,
} from "@phosphor-icons/react";

const SORT_FIELD_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "item no", label: "Item Number" },
  { value: "CBM", label: "CBM" },
];

export function DashboardFilterBar({
  viewMode,
  onViewModeChange,
  categoryOptions,
  category,
  onCategoryChange,
  subCategoryOptions = [],
  subCategory = "",
  onSubCategoryChange,
  sortField,
  onSortFieldChange,
  sortOrder,
  onToggleSortOrder,
  cacheStatus,
  showAll = false,
  onToggleShowAll,
  showBestSellingFilter = false,
  bestOnlyFilter = false,
  onToggleBestOnly,
  showBlockedFilter = false,
  blockedOnlyFilter = false,
  onToggleBlockedOnly,
  bestSellingMode = false,
  showSelectionButtons = false,
  showGTHistory = true,
  actionSlot = null,
  selectedCount = 0,
  onSelectAll,
  onClearSelection,
  onPrint,
  onOpenGTHistory,
  showSelectedOnlyToggle = false,
  showSelectedOnly = false,
  onToggleShowSelectedOnly,
  searchInput,
  onSearchInputChange,
  cbmMinInput,
  onCbmMinChange,
  cbmMaxInput,
  onCbmMaxChange,
  qtyMinInput,
  onQtyMinChange,
  qtyMaxInput,
  onQtyMaxChange,
  dateFromInput,
  onDateFromChange,
  dateToInput,
  onDateToChange,
  onSearchSubmit,
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-300 px-4 py-3 lg:px-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-sm border border-zinc-300 bg-zinc-50 p-0.5" data-testid="view-mode-toggle">
          <button
            type="button"
            onClick={() => onViewModeChange("table")}
            className={`rounded-sm px-2 py-1.5 transition-colors duration-150 ${
              viewMode === "table" ? "bg-blue-600 text-white" : "text-zinc-500 hover:bg-zinc-100"
            }`}
            data-testid="view-mode-table-button"
          >
            <TableIcon size={14} />
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange("grid")}
            className={`rounded-sm px-2 py-1.5 transition-colors duration-150 ${
              viewMode === "grid" ? "bg-blue-600 text-white" : "text-zinc-500 hover:bg-zinc-100"
            }`}
            data-testid="view-mode-grid-button"
          >
            <SquaresFour size={14} />
          </button>
        </div>

        {categoryOptions.length > 0 && (
          <Select value={category || "all"} onValueChange={onCategoryChange}>
            <SelectTrigger className="w-40 rounded-sm border-zinc-300 text-sm" data-testid="category-filter-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categoryOptions.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {subCategoryOptions.length > 0 && (
          <Select value={subCategory || "all"} onValueChange={onSubCategoryChange}>
            <SelectTrigger
              className="w-44 rounded-sm border-zinc-300 text-sm"
              data-testid="sub-category-filter-select"
              title={category ? `Sub-categories within ${category}` : "All sub-categories"}
            >
              <SelectValue placeholder="Sub-Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sub-Categories</SelectItem>
              {subCategoryOptions.map((c) => (
                <SelectItem key={c} value={c} data-testid={`sub-category-option-${c}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {!bestSellingMode && (
          <>
            <Select value={sortField} onValueChange={onSortFieldChange}>
              <SelectTrigger className="w-36 rounded-sm border-zinc-300 text-sm" data-testid="sort-field-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_FIELD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="rounded-sm border-zinc-300" onClick={onToggleSortOrder} data-testid="sort-order-toggle-button">
              <ArrowsDownUp size={14} className="mr-1.5" />
              {sortOrder === "ascend" ? "↑" : "↓"}
            </Button>
          </>
        )}
        {cacheStatus && (
          <span className="text-[10px] uppercase tracking-[0.05em] text-zinc-400" data-testid="cache-status-label">
            {cacheStatus.syncing
              ? `Syncing with FileMaker...${cacheStatus.sync_phase ? ` ${cacheStatus.sync_phase}` : ""}${
                  cacheStatus.sync_total > 0 ? ` (${cacheStatus.sync_done}/${cacheStatus.sync_total})` : ""
                }`
              : cacheStatus.last_synced_at
              ? `Data as of: ${new Date(cacheStatus.last_synced_at).toLocaleString()}`
              : "Data not yet synced"}
          </span>
        )}
        {cacheStatus && !cacheStatus.syncing && cacheStatus.last_sync_error && (
          <span
            className="max-w-md truncate text-[10px] font-medium uppercase tracking-[0.05em] text-red-500"
            title={cacheStatus.last_sync_error}
            data-testid="cache-sync-error-label"
          >
            Last sync failed: {cacheStatus.last_sync_error.replace(/^\d{4}-\d{2}-\d{2}T[\d:.]+(?:\+\d{2}:\d{2}|Z)?:\s*/, "").slice(0, 120)}
          </span>
        )}
        {bestSellingMode ? (
          <>
            {showGTHistory && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm border-zinc-300"
                onClick={onOpenGTHistory}
                data-testid="gt-history-button"
              >
                <ClockCounterClockwise size={14} className="mr-1.5" />
                GT Selling History
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm border-zinc-300"
              onClick={onSelectAll}
              data-testid="select-all-button"
            >
              <Selection size={14} className="mr-1.5" />
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm border-zinc-300"
              onClick={onClearSelection}
              disabled={selectedCount === 0}
              data-testid="clear-selection-button"
            >
              <SelectionSlash size={14} className="mr-1.5" />
              Deselect All
            </Button>
            <Button
              size="sm"
              className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
              onClick={onPrint}
              disabled={selectedCount === 0}
              data-testid="print-selected-button"
            >
              <Printer size={14} className="mr-1.5" />
              Print{selectedCount > 0 ? ` (${selectedCount})` : ""}
            </Button>
            {showSelectedOnlyToggle && (
              <Button
                variant={showSelectedOnly ? "default" : "outline"}
                size="sm"
                className={`rounded-sm ${
                  showSelectedOnly
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "border-zinc-300"
                }`}
                onClick={onToggleShowSelectedOnly}
                disabled={selectedCount === 0 && !showSelectedOnly}
                title="Show only the items you have selected"
                data-testid="show-selected-only-button"
              >
                <Eye size={14} className="mr-1.5" />
                {showSelectedOnly
                  ? `Selected Only: ON (${selectedCount})`
                  : "Show Selected Only"}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant={showAll ? "default" : "outline"}
              size="sm"
              className={`rounded-sm ${showAll ? "bg-blue-600 text-white hover:bg-blue-700" : "border-zinc-300"}`}
              onClick={onToggleShowAll}
              title="Show every record, ignoring the qty > 240 / CBM > 1 inventory baseline"
              data-testid="show-all-button"
            >
              <ListMagnifyingGlass size={14} className="mr-1.5" />
              {showAll ? "Show All: ON" : "Show All"}
            </Button>
            {showBestSellingFilter && (
              <Button
                variant={bestOnlyFilter ? "default" : "outline"}
                size="sm"
                className={`rounded-sm ${bestOnlyFilter ? "bg-amber-500 text-white hover:bg-amber-600" : "border-zinc-300"}`}
                onClick={onToggleBestOnly}
                title="Show only Best Selling items"
                data-testid="best-selling-filter-button"
              >
                <TrendUp size={14} className="mr-1.5" />
                {bestOnlyFilter ? "Best Selling: ON" : "Best Selling"}
              </Button>
            )}
            {showBlockedFilter && (
              <Button
                variant={blockedOnlyFilter ? "default" : "outline"}
                size="sm"
                className={`rounded-sm ${blockedOnlyFilter ? "bg-red-600 text-white hover:bg-red-700" : "border-zinc-300"}`}
                onClick={onToggleBlockedOnly}
                title="Show all blocked products"
                data-testid="show-blocked-items-button"
              >
                <Prohibit size={14} className="mr-1.5" />
                {blockedOnlyFilter ? "Blocked Items: ON" : "Show Block Item"}
              </Button>
            )}
            {showSelectionButtons && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-sm border-zinc-300"
                  onClick={onSelectAll}
                  title="Select every item on this page"
                  data-testid="select-all-button"
                >
                  <Selection size={14} className="mr-1.5" />
                  Select All{selectedCount > 0 ? ` (${selectedCount})` : ""}
                </Button>
                {selectedCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm border-zinc-300"
                    onClick={onClearSelection}
                    data-testid="clear-selection-button"
                  >
                    <SelectionSlash size={14} className="mr-1.5" />
                    Deselect All
                  </Button>
                )}
              </>
            )}
            {actionSlot}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={onSearchSubmit} className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={searchInput}
              onChange={(e) => onSearchInputChange(e.target.value)}
              placeholder=""
              className="w-56 rounded-sm border-zinc-300 pl-8 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="sku-search-input"
            />
          </div>
          {!bestSellingMode && (
            <>
              <div className="flex items-center gap-1" title="Show products where CBM is within this range (combined with QTY range using OR)">
                <span className="text-xs text-zinc-500">CBM</span>
                <Input
                  type="number"
                  value={cbmMinInput}
                  onChange={(e) => onCbmMinChange(e.target.value)}
                  placeholder="min"
                  className="w-16 rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                  data-testid="cbm-min-filter-input"
                />
                <span className="text-xs text-zinc-400">to</span>
                <Input
                  type="number"
                  value={cbmMaxInput}
                  onChange={(e) => onCbmMaxChange(e.target.value)}
                  placeholder="max"
                  className="w-16 rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                  data-testid="cbm-max-filter-input"
                />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide text-zinc-400" data-testid="cbm-qty-or-label">
                or
              </span>
              <div className="flex items-center gap-1" title="Show products where QTY is within this range (combined with CBM range using OR)">
                <span className="text-xs text-zinc-500">QTY</span>
                <Input
                  type="number"
                  value={qtyMinInput}
                  onChange={(e) => onQtyMinChange(e.target.value)}
                  placeholder="min"
                  className="w-16 rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                  data-testid="qty-min-filter-input"
                />
                <span className="text-xs text-zinc-400">to</span>
                <Input
                  type="number"
                  value={qtyMaxInput}
                  onChange={(e) => onQtyMaxChange(e.target.value)}
                  placeholder="max"
                  className="w-16 rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
                  data-testid="qty-max-filter-input"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-1" title="Show products whose date is within this range (combined with the CBM/QTY filters using AND)">
            <span className="text-xs text-zinc-500">{bestSellingMode ? "Distribution Date" : "Date"}</span>
            <Input
              type="date"
              value={dateFromInput}
              onChange={(e) => onDateFromChange(e.target.value)}
              className="w-[8.5rem] rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="date-from-filter-input"
            />
            <span className="text-xs text-zinc-400">to</span>
            <Input
              type="date"
              value={dateToInput}
              onChange={(e) => onDateToChange(e.target.value)}
              className="w-[8.5rem] rounded-sm border-zinc-300 text-sm focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="date-to-filter-input"
            />
          </div>
          <Button type="submit" size="sm" variant="outline" className="rounded-sm border-zinc-300" data-testid="search-submit-button">
            Search
          </Button>
        </form>
      </div>
    </div>
  );
}
